import type {
  ClockDriftResult,
  ServerClientSyncPayload,
} from "@multisystemsuite/timezone-engine-shared-types";
import { compareTimezones } from "./comparison.js";
import { detectServerTimezone } from "./detection.js";
import { formatDate } from "./formatting.js";
import { safeTimezoneFallback } from "./validation.js";

function toDate(input: Date | string | number): Date {
  if (input instanceof Date) return input;
  return new Date(input);
}

/** Detect client/server clock drift for distributed sync scenarios */
export function detectClockDrift(
  clientTime: Date | string | number,
  serverTime: Date | string | number = new Date(),
  toleranceMs = 5000,
): ClockDriftResult {
  const client = toDate(clientTime);
  const server = toDate(serverTime);
  const driftMs = client.getTime() - server.getTime();

  return {
    driftMs,
    driftSeconds: driftMs / 1000,
    withinTolerance: Math.abs(driftMs) <= toleranceMs,
    toleranceMs,
    serverTime: server,
    clientTime: client,
  };
}

/** Synchronize server and client timezone context with drift detection */
export function syncServerTime(options: {
  clientTimezone: string;
  serverTimezone?: string;
  clientTime?: Date | string | number;
  toleranceMs?: number;
}): ServerClientSyncPayload & { drift: ClockDriftResult } {
  const clientTimezone = safeTimezoneFallback(options.clientTimezone);
  const serverTimezone = safeTimezoneFallback(
    options.serverTimezone ?? detectServerTimezone().timezone,
  );
  const now = new Date();
  const clientTime = options.clientTime ? toDate(options.clientTime) : now;
  const drift = detectClockDrift(clientTime, now, options.toleranceMs ?? 5000);
  const comparison = compareTimezones(serverTimezone, clientTimezone, now);

  return {
    serverTimezone,
    clientTimezone,
    serverTime: formatDate(now, { format: "ISO", timezone: serverTimezone }),
    clientTime: formatDate(clientTime, { format: "ISO", timezone: clientTimezone }),
    offsetDifferenceMinutes: comparison.offsetDifferenceMinutes,
    driftMs: drift.driftMs,
    driftWithinTolerance: drift.withinTolerance,
    drift,
  };
}
